/**
 * PROD-2846: the lab desk lists an encounter's lab orders by requisition,
 * one block per lab (Scrips.Patient #1022, PROD-2841).
 *
 * - A row joins a block when it carries a `requisitionId` and its encounter's
 *   `LabRequisitions` read did not report `storeAvailable: false`.
 * - Rows without a requisition (legacy, or before AddLabRequisitions.sql has
 *   run) stay plain rows, so the desk then looks exactly as before.
 * - A block sits where its first row was on the page.
 * - Patient's choice of lab: the patient takes the order to any lab. The
 *   clinic sends nothing and bills nothing.
 * - PROD-3115: a clinic panel can send some tests to another lab (each test
 *   row carries its own requisition). The order stays in its own lab's block
 *   with only that lab's tests; each other lab gets a "split part" row in its
 *   block, with its own status (from its test rows) and its own actions.
 */
import {
  LAB_FULFILMENT_MODE,
  LAB_REQUEST_SOURCE,
  type EncounterLabRequisitions,
  type LabRequisition,
  type PatientLabOrderDetail,
} from '@/types/scheduling-api';
import {
  ownPartRow,
  partOfLabel,
  splitPartRow,
  testsByRequisition,
  type LabSplitPart,
} from './lab-split-parts';


export const PATIENT_CHOICE_LABEL = "Patient's choice of lab";
export const PATIENT_CHOICE_HEADER =
  "Patient's choice of lab: the patient takes this order to any lab";
export const PATIENT_CHOICE_NOTE = 'Not sent and not billed by the clinic';
export const ADDED_BY_LAB_LABEL = 'Added by the lab';

/** A lab desk row: an order, or (PROD-3115) an order's part at another lab. */
export type LabDeskRow = PatientLabOrderDetail & { splitPart?: LabSplitPart };

export type LabDeskEntry =
  | { kind: 'row'; row: LabDeskRow }
  | {
      kind: 'requisition';
      requisitionId: string;
      encounterId: string | null;
      patientChoice: boolean;
      labName: string | null;
      rows: LabDeskRow[];
    };

/** Filters the server applied to the orders; split parts follow them too. */
export interface LabDeskGroupFilters {
  labVendorId?: string | null;
  status?: string | null;
}

export function isPatientChoice(row: PatientLabOrderDetail): boolean {
  return row.fulfilmentMode === LAB_FULFILMENT_MODE.PatientChoice;
}

/** Reflex (and unsolicited) tests: performed by the lab, not ordered. */
export function isAddedByLab(row: PatientLabOrderDetail): boolean {
  return (
    row.source === LAB_REQUEST_SOURCE.Reflex ||
    row.source === LAB_REQUEST_SOURCE.Unsolicited
  );
}

/** Encounters on the page that have at least one row in a requisition. */
export function encountersWithRequisitions(
  rows: PatientLabOrderDetail[],
): { patientId: string; encounterId: string }[] {
  const seen = new Map<string, { patientId: string; encounterId: string }>();
  for (const r of rows) {
    if (r.requisitionId && r.patientId && r.encounterId && !seen.has(r.encounterId)) {
      seen.set(r.encounterId, { patientId: r.patientId, encounterId: r.encounterId });
    }
  }
  return [...seen.values()];
}

export function groupLabDeskRows(
  rows: PatientLabOrderDetail[],
  requisitionsByEncounter: Map<string, EncounterLabRequisitions>,
  filters: LabDeskGroupFilters = {},
): LabDeskEntry[] {
  const entries: LabDeskEntry[] = [];
  const blocks = new Map<string, Extract<LabDeskEntry, { kind: 'requisition' }>>();

  const addToBlock = (
    reqId: string,
    row: LabDeskRow,
    read: EncounterLabRequisitions | undefined,
  ) => {
    const existing = blocks.get(reqId);
    if (existing) {
      existing.rows.push(row);
      return;
    }
    const requisition: LabRequisition | undefined = read?.requisitions?.find(
      (q) => q.id === reqId,
    );
    const patientChoice = requisition
      ? requisition.fulfilmentMode === LAB_FULFILMENT_MODE.PatientChoice
      : isPatientChoice(row);
    const block: Extract<LabDeskEntry, { kind: 'requisition' }> = {
      kind: 'requisition',
      requisitionId: reqId,
      encounterId: row.encounterId ?? null,
      patientChoice,
      labName: patientChoice
        ? null
        : requisition?.labVendorName?.trim() || row.labVendorName?.trim() || null,
      rows: [row],
    };
    blocks.set(reqId, block);
    entries.push(block);
  };

  for (const row of rows) {
    const reqId = row.requisitionId;
    const read = row.encounterId
      ? requisitionsByEncounter.get(row.encounterId)
      : undefined;
    if (!reqId || read?.storeAvailable === false) {
      entries.push({ kind: 'row', row });
      continue;
    }

    // PROD-3115: tests of this order that sit in another lab's requisition.
    const byRequisition = testsByRequisition(row, read);
    const parts = [...byRequisition.entries()].filter(([id]) => id !== reqId);
    if (parts.length === 0) {
      addToBlock(reqId, row, read);
      continue;
    }

    // The order lists only its own lab's tests. When its own lab has none of
    // them, every test is in a part, so the order row is not shown at all.
    // PROD-3157: the order claims no lab vendor, so the row takes its own
    // requisition's lab, and the vendor filter applies to it like to a part.
    const own = byRequisition.get(reqId);
    if (own && own.tests.length > 0) {
      const ownRow = ownPartRow(row, own.requisition, own.tests);
      if (!filters.labVendorId || ownRow.labVendorId === filters.labVendorId) {
        addToBlock(reqId, ownRow, read);
      }
    }
    for (const [partReqId, { requisition, tests }] of parts) {
      const part = splitPartRow(row, requisition, tests);
      if (filters.labVendorId && part.labVendorId !== filters.labVendorId) continue;
      if (filters.status && part.orderStatus !== filters.status) continue;
      addToBlock(partReqId, part, read);
    }
  }
  return entries;
}

/** The block's header row, spanning the whole table. */
export function RequisitionHeaderRow({
  entry,
  colSpan,
}: {
  entry: Extract<LabDeskEntry, { kind: 'requisition' }>;
  colSpan: number;
}) {
  const count = entry.rows.length;
  const orders = `${count} ${count === 1 ? 'order' : 'orders'}`;
  return (
    <tr
      data-testid="lab-requisition-header"
      className="border-t border-border-default bg-grey-100/60"
    >
      <td colSpan={colSpan} className="px-3 py-2">
        {entry.patientChoice ? (
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm font-semibold text-[var(--color-brand-depth)]">
              {PATIENT_CHOICE_HEADER}
            </span>
            <span className="text-xs text-text-muted">
              {PATIENT_CHOICE_NOTE} · {orders}
            </span>
          </div>
        ) : (
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm font-semibold text-[var(--color-brand-depth)]">
              {entry.labName ?? '—'}
            </span>
            <span className="text-xs text-text-muted">{orders}</span>
          </div>
        )}
      </td>
    </tr>
  );
}

/** PROD-3115: small muted marker on a split part: the order it belongs to. */
export function PartOfOrderMarker({ orderName }: { orderName: string | null }) {
  return (
    <span className="inline-flex items-center rounded bg-grey-100 px-2 py-0.5 text-xs font-medium text-text-muted whitespace-nowrap shrink-0">
      {partOfLabel(orderName)}
    </span>
  );
}

/** Small muted marker next to the test name. */
export function AddedByLabMarker() {
  return (
    <span className="inline-flex items-center rounded bg-grey-100 px-2 py-0.5 text-xs font-medium text-text-muted whitespace-nowrap shrink-0">
      {ADDED_BY_LAB_LABEL}
    </span>
  );
}
